import { FunctionComponent, PropsWithChildren, ReactNode } from 'react'

import { Box, Stack, Text, TextProps, useTheme, useToast } from '@chakra-ui/react'

import { CopyIcon } from '@/common/components/CustomIcon/CopyIcon'

interface CopyableAddressProps {
  text?: string
}

export interface AddressProps extends Omit<TextProps, 'onCopy'> {
  startLength?: number
  lastLength?: number
  truncate?: boolean
  ellipsis?: ReactNode
  copyable?: CopyableAddressProps | boolean
  copyIfClicked?: boolean
  hasIcon?: boolean
}

export const Address: FunctionComponent<PropsWithChildren<AddressProps>> = ({
  children,
  copyIfClicked = false,
  copyable,
  ellipsis = '..',
  fontSize = 'md',
  fontWeight = 900,
  hasIcon = true,
  lastLength = -4,
  startLength = 5,
  truncate = true,
  ...props
}): JSX.Element => {
  const toast = useToast()
  const { colors } = useTheme()
  const string = (copyable as CopyableAddressProps)?.text || children.toString()
  const startLetter = string.slice(0, startLength)
  const lastLetter = string.slice(lastLength)
  const textColor = colors.grey[1000]

  const handleCopy = (): void => {
    navigator.clipboard.writeText(string)
    toast({
      position: 'top',
      title: 'Copied to clipboard!',
      status: 'success',
    })
  }

  return (
    <Stack
      alignItems="center"
      direction="row"
      gap="2px"
      onClick={copyIfClicked ? () => handleCopy() : undefined}
    >
      <Text
        className="font-black"
        color={textColor}
        fontSize={fontSize}
        fontWeight={fontWeight}
        {...props}
      >
        <span className="text-sm md:text-base">
          {truncate ? startLetter + ellipsis + lastLetter : children}
        </span>
      </Text>
      {Boolean(copyable) && hasIcon && (
        <Box aria-label="copy" as="button" onClick={handleCopy}>
          <CopyIcon size="16" />
        </Box>
      )}
    </Stack>
  )
}
